import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { mockProjects, mockUsers, mockTasks, mockCompanies } from '../mock/data';
import { 
  Calendar, Users as UsersIcon, FileText, Target, ArrowLeft, Edit, 
  AlertTriangle, CheckCircle2, Clock, AlertCircle, History,
  UserPlus, UserMinus, X, Pause, XCircle
} from 'lucide-react';
import ProjectSummary from '../components/ProjectSummary';
import ProjectFiles from '../components/ProjectFiles';
import SectionHeader from '../components/shared/SectionHeader';

// Calculate project status based on tasks and dates
const calculateProjectStatus = (project, tasks) => {
  // If manual status is set, use it
  if (project.manualStatus) {
    return project.manualStatus;
  }
  
  const today = new Date();
  const endDate = new Date(project.endDate);
  
  // Check if project is past due date
  if (today > endDate && project.progress < 100) {
    return 'late';
  }
  
  // Check if any task is late
  const projectTasks = tasks.filter(t => t.projectId === project.id);
  const hasLateTasks = projectTasks.some(task => {
    const taskDue = new Date(task.dueDate);
    return taskDue < today && task.status !== 'completed';
  });
  
  if (hasLateTasks) {
    return 'late-task';
  }
  
  // Check if completed
  if (project.progress === 100) {
    return 'completed';
  }
  
  // Otherwise on time
  return 'on-time';
};

const getStatusConfig = (status) => {
  switch (status) {
    case 'late':
      return { 
        label: 'Late', 
        color: 'bg-red-500/20 text-red-400 border-red-500/30',
        icon: AlertCircle,
        gradient: 'from-red-500 to-rose-400'
      };
    case 'late-task':
      return { 
        label: 'Late Task', 
        color: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
        icon: AlertTriangle,
        gradient: 'from-orange-500 to-amber-400'
      };
    case 'on-time':
      return { 
        label: 'On Time', 
        color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
        icon: CheckCircle2,
        gradient: 'from-emerald-500 to-green-400'
      };
    case 'on-hold':
      return { 
        label: 'On Hold', 
        color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
        icon: Pause,
        gradient: 'from-amber-500 to-yellow-400'
      };
    case 'cancelled':
      return { 
        label: 'Cancelled', 
        color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
        icon: XCircle,
        gradient: 'from-slate-500 to-gray-400'
      };
    case 'completed':
      return { 
        label: 'Completed', 
        color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        icon: CheckCircle2,
        gradient: 'from-blue-500 to-cyan-400'
      };
    default:
      return { 
        label: 'Unknown', 
        color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
        icon: Clock,
        gradient: 'from-slate-500 to-gray-400'
      };
  }
};

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const project = mockProjects.find(p => p.id === projectId);
  const company = mockCompanies.find(c => c.id === project?.companyId);
  const escalatedTo = project?.escalatedTo ? mockUsers.find(u => u.id === project.escalatedTo) : null;

  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showEscalationDialog, setShowEscalationDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);

  if (!project) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    );
  }

  const teamMembers = project.teamMembers.map(id => mockUsers.find(u => u.id === id));
  
  // Calculate project status
  const projectStatus = calculateProjectStatus(project, mockTasks);
  const statusConfig = getStatusConfig(projectStatus);
  const StatusIcon = statusConfig.icon;
  
  // Calculate days remaining
  const today = new Date();
  const endDate = new Date(project.endDate);
  const startDate = new Date(project.startDate);
  const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  const daysElapsed = totalDays - daysRemaining;

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/projects')}
          className="mb-4 text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>
        
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: project.color }}>
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-foreground">{project.name}</h1>
                {/* Project Status Badge (replaces category) */}
                <Badge className={statusConfig.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">{project.description}</p>
              <p className="text-sm text-muted-foreground mt-1">{company?.name}</p>
            </div>
          </div>
          
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Edit className="w-4 h-4 mr-2" />
            Edit Project
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Combined Progress + Timeline Card */}
        <Card className="bg-card border-border overflow-hidden">
          <CardContent className="p-6">
            {/* Progress Section */}
            <p className="text-sm text-muted-foreground mb-2">Progress</p>
            <div className="flex items-end justify-between mb-4">
              <p className="text-4xl font-bold text-foreground">{project.progress}%</p>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Target</p>
                <p className="text-lg font-semibold text-foreground">100%</p>
              </div>
            </div>
            <div className="relative h-3 bg-muted rounded-full overflow-hidden mb-6">
              <div 
                className="absolute inset-y-0 left-0 rounded-full transition-all"
                style={{
                  width: `${project.progress}%`,
                  background: `linear-gradient(90deg, ${project.color}ee, ${project.color}88)`
                }}
              />
            </div>

            {/* Timeline Section */}
            <p className="text-sm text-muted-foreground mb-4">Timeline</p>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute top-3 left-0 right-0 h-1 bg-muted rounded-full">
                <div 
                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-secondary"
                  style={{ width: `${Math.min(Math.max((daysElapsed / totalDays) * 100, 0), 100)}%` }}
                />
              </div>
              
              {/* Timeline points - order changes based on overdue status */}
              <div className="flex justify-between relative">
                {/* Start Date */}
                <div className="flex flex-col items-center">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center mb-2 z-10">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                  <p className="text-xs text-muted-foreground">Start</p>
                  <p className="text-sm font-semibold text-foreground">
                    {new Date(project.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>

                {daysRemaining < 0 ? (
                  <>
                    {/* Due Date - amber color when overdue */}
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-2 z-10 ${projectStatus === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <p className="text-xs text-muted-foreground">Due</p>
                      <p className="text-sm font-semibold text-amber-500">
                        {new Date(project.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Today - red when overdue */}
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center mb-2 z-10 animate-pulse">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <p className="text-xs text-muted-foreground">Today</p>
                      <p className="text-sm font-semibold text-red-500">
                        {Math.abs(daysRemaining)}d overdue
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Today */}
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center mb-2 z-10 animate-pulse">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <p className="text-xs text-muted-foreground">Today</p>
                      <p className="text-sm font-semibold text-foreground">
                        {daysRemaining}d left
                      </p>
                    </div>

                    {/* Due Date - amber color */}
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-2 z-10 ${projectStatus === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <p className="text-xs text-muted-foreground">Due</p>
                      <p className="text-sm font-semibold text-amber-500">
                        {new Date(project.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Card */}
        <Card className="bg-card border-border overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">Team Members</p>
              <Badge variant="secondary" className="text-xs">
                {teamMembers.length} members
              </Badge>
            </div>
            
            {/* Team Avatars */}
            <div className="flex flex-wrap gap-3 mb-6">
              {teamMembers.map((member) => (
                <div key={member?.id} className="flex flex-col items-center">
                  <Avatar className="w-12 h-12 border-2 border-background">
                    <AvatarImage src={member?.avatar} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-sm">
                      {member?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-xs text-muted-foreground mt-1 max-w-[60px] truncate">
                    {member?.name?.split(' ')[0]}
                  </p>
                </div>
              ))}
            </div>

            {/* Manage Team Button */}
            <Button 
              variant="outline" 
              className="w-full" 
              size="sm"
              onClick={() => setShowTeamDialog(true)}
            >
              <UsersIcon className="w-4 h-4 mr-2" />
              Manage Team
            </Button>
          </CardContent>
        </Card>

        {/* Escalation Card (replaces Status Card) */}
        <Card className={`border-border overflow-hidden ${project.isEscalated ? 'bg-amber-500/10 border-amber-500/30' : 'bg-card'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">Escalation</p>
              {project.escalationHistory?.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setShowHistoryDialog(true)}
                >
                  <History className="w-4 h-4 mr-1" />
                  History ({project.escalationHistory.length})
                </Button>
              )}
            </div>
            
            {project.isEscalated ? (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">Escalated</p>
                    <p className="text-xs text-muted-foreground">to {escalatedTo?.name}</p>
                  </div>
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={escalatedTo?.avatar} />
                    <AvatarFallback>{escalatedTo?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                </div>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{project.escalationReason}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => setShowEscalationDialog(true)}
                >
                  Manage Escalation
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                </div>
                <p className="font-medium text-foreground mb-1">Not Escalated</p>
                <p className="text-sm text-muted-foreground mb-3">Project is running smoothly</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                  onClick={() => setShowEscalationDialog(true)}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Escalate Project
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tags Row */}
      {project.tags?.length > 0 && (
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-muted-foreground">Tags:</span>
          {project.tags.map(tag => (
            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
          ))}
        </div>
      )}

      {/* Tabs Section */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <ProjectSummary project={project} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionHeader
              title="Scope of Work"
              icon={Target}
              iconColor="text-primary"
              gradient="from-primary/10 via-secondary/10 to-accent/10"
            >
              <p className="text-foreground leading-relaxed">{project.scopeOfWork}</p>
            </SectionHeader>

            <SectionHeader
              title="Deliverables"
              icon={FileText}
              iconColor="text-secondary"
              gradient="from-secondary/10 via-accent/10 to-primary/10"
            >
              <p className="text-foreground leading-relaxed">{project.deliverables}</p>
            </SectionHeader>
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <Card className="bg-card border-border">
            <CardContent className="p-8 text-center text-muted-foreground">
              <p>Tasks view - Click below to see full task list</p>
              <Button
                className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                View Task List
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="files">
          <ProjectFiles projectId={project.id} />
        </TabsContent>

        <TabsContent value="timeline">
          <Card className="bg-card border-border">
            <CardContent className="p-8 text-center text-muted-foreground">
              <p>Interactive Gantt chart view</p>
              <Button
                className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => navigate(`/timeline/${project.id}`)}
              >
                View Timeline
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Team Management Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Manage Team Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Add Member Section */}
            <div>
              <Label className="text-foreground">Add Team Member</Label>
              <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                {mockUsers.filter(u => !project.teamMembers.includes(u.id)).map(user => (
                  <div 
                    key={user.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => {
                      // Add member to project (mock - would be API call)
                      project.teamMembers.push(user.id);
                      setShowTeamDialog(false);
                      setTimeout(() => setShowTeamDialog(true), 100);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user.avatar} />
                        <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.role}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="text-primary hover:bg-primary/10">
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {mockUsers.filter(u => !project.teamMembers.includes(u.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">All users are already team members</p>
                )}
              </div>
            </div>
            
            {/* Current Members Section */}
            <div>
              <Label className="text-foreground">Current Team ({teamMembers.length} members)</Label>
              <div className="mt-2 space-y-2 max-h-[250px] overflow-y-auto">
                {teamMembers.map(member => (
                  <div key={member?.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={member?.avatar} />
                        <AvatarFallback>{member?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground">{member?.name}</p>
                        <p className="text-xs text-muted-foreground">{member?.role}</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        // Remove member from project (mock - would be API call)
                        const index = project.teamMembers.indexOf(member.id);
                        if (index > -1) {
                          project.teamMembers.splice(index, 1);
                          setShowTeamDialog(false);
                          setTimeout(() => setShowTeamDialog(true), 100);
                        }
                      }}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            <Button 
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" 
              onClick={() => setShowTeamDialog(false)}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Escalation Dialog */}
      <Dialog open={showEscalationDialog} onOpenChange={setShowEscalationDialog}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {project.isEscalated ? 'Manage Escalation' : 'Escalate Project'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-foreground">Escalate To</Label>
              <Select defaultValue={project.escalatedTo || ''}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select person" />
                </SelectTrigger>
                <SelectContent>
                  {mockUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {user.name} - {user.role}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-foreground">Escalation Reason</Label>
              <Textarea 
                placeholder="Describe the reason for escalation..."
                className="mt-1"
                rows={4}
                defaultValue={project.escalationReason || ''}
              />
            </div>
            
            <div className="flex gap-2">
              {project.isEscalated && (
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    alert('Escalation will be removed with backend');
                    setShowEscalationDialog(false);
                  }}
                >
                  Remove Escalation
                </Button>
              )}
              <Button 
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => {
                  alert('Escalation will be saved with backend');
                  setShowEscalationDialog(false);
                }}
              >
                {project.isEscalated ? 'Update Escalation' : 'Escalate Project'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Escalation History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Escalation History</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {project.escalationHistory?.length > 0 ? (
              <div className="space-y-4">
                {project.escalationHistory.map((esc, idx) => {
                  const escTo = mockUsers.find(u => u.id === esc.escalatedTo);
                  const resolvedBy = mockUsers.find(u => u.id === esc.resolvedBy);
                  
                  return (
                    <div key={esc.id || idx} className="p-4 rounded-lg bg-muted/50 border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={escTo?.avatar} />
                            <AvatarFallback>{escTo?.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-foreground">{escTo?.name}</p>
                            <p className="text-xs text-muted-foreground">{new Date(esc.date).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-400">Resolved</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{esc.reason}</p>
                      {esc.resolvedDate && (
                        <p className="text-xs text-muted-foreground">
                          Resolved on {new Date(esc.resolvedDate).toLocaleDateString()} by {resolvedBy?.name}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No escalation history</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetails;
